<template>
  <transition name="d-modal">
    <div class="zen-export-overlay">
      <div class="zen-export">
        <div class="zen-export__header">
          <h2>{{ t.title }}</h2>
          <div class="zen-export__header-actions">
            <div v-if="!isLoading" class="zen-export__duration">
              <div class="zen-export__duration-row">
                <label class="zen-export__duration-label" for="zen-export-duration-input">
                  {{ $t('vue2.set_standard_duration.standard_duration_for_the_school') }}
                  <span
                    class="zen-export__duration-info"
                    :content="$t('vue2.set_standard_duration.set_standard_length_for_zen')"
                    v-tippy="{
                      arrow: true,
                      placement: 'bottom',
                      maxWidth: 320,
                      boundary: 'window',
                      animateFill: false,
                      animation: 'shift-away',
                      zIndex: '999999992'
                    }">ⓘ</span>
                </label>
                <div class="zen-export__duration-inputwrap">
                  <input
                    id="zen-export-duration-input"
                    ref="durationInput"
                    type="number"
                    class="zen-export__duration-input"
                    v-model.number="durationInputMinutes"
                    maxlength="4"
                    @blur="onStandardDurationBlur"
                    @focus="onStandardDurationFocus">
                  <span class="zen-export__duration-suffix">{{ $t('vue2.set_standard_duration.minutes') }}</span>
                </div>
              </div>
              <p v-if="!isAValidStandardDuration" class="zen-export__duration-error">
                {{ $t('vue2.set_standard_duration.the_number_must_be_divisible_by_five') }}
              </p>
            </div>
            <span class="zen-export__close icon-cross2" @click="$emit('close')"></span>
          </div>
        </div>

        <div class="zen-export__tabs">
          <Tabs
            :tabs="calendarTabs"
            :selected="activeTab"
            :isLarge="true"
            @select="activeTab = $event"
          />
          <DoFiltersFilter
            v-if="!isLoading"
            :title="t.filter_title"
            :tags="uniqueSubjectTitles"
            :selectedTags="selectedSubjectTitleFilters"
            :numberOfSelectedTagsToShow="1"
            @select="toggleSelectedSubjectTitleFilters"
            @clear="clearSelectedSubjectTitleFilters"
          >
          </DoFiltersFilter>
          <DoFiltersFilter
            v-if="!isLoading"
            :title="t.filter_type"
            :tags="uniqueSubjectTypeFilterTags"
            :selectedTags="selectedSubjectTypeFilters"
            :numberOfSelectedTagsToShow="1"
            @select="toggleSelectedSubjectTypeFilters"
            @clear="clearSelectedSubjectTypeFilters"
          >
          </DoFiltersFilter>
          <DoFiltersFilter
            v-if="!isLoading"
            :title="calendarFilterTitle"
            :tags="uniqueCalendarNames"
            :selectedTags="selectedCalendarNameFilters"
            :numberOfSelectedTagsToShow="1"
            @select="toggleSelectedCalendarNameFilters"
            @clear="clearSelectedCalendarNameFilters"
          >
          </DoFiltersFilter>
        </div>
        <div class="zen-export__toolbar">
          <div class="zen-export__toolbar-left">
            <span class="zen-export__toggle-all" @click="toggleAll">
              {{ allChecked ? t.deselect_all : t.select_all }}
            </span>
            <span class="zen-export__count zen-export__count--tab">
              {{ tabCountText }}
            </span>
          </div>
          <span class="zen-export__count">
            {{ totalCountText }}
          </span>
        </div>

        <Loader v-if="isLoading" bgColor="#f6f9fc"></Loader>
        <template v-else>
          <div class="zen-export__body">
            <div class="zen-export__tables" v-if="groupedRows.length > 0">
              <table
                class="do-table zen-table"
                v-for="group in groupedRows"
                :key="group.calendarId">
                <thead>
                  <tr>
                    <th class="zen-table__th zen-table__th--check" @click="toggleGroup(group)">
                      <div class="d-checkbox" :class="{'d-checkbox--active': isGroupAllChecked(group)}"></div>
                    </th>
                    <th style="width: 24px;"></th>
                    <th class="zen-table__th zen-table__th--name" colspan="1">
                      {{ group.calendarName }}
                    </th>
                    <th class="zen-table__th">{{ t.th_type }}</th>
                    <th class="zen-table__th">{{ t.th_teachers }}</th>
                    <th class="zen-table__th">{{ t.th_rooms }}</th>
                    <th class="zen-table__th">{{ t.th_klasses }}</th>
                    <th class="zen-table__th zen-table__th--numeric">{{ t.th_hours }}</th>
                    <th class="zen-table__th zen-table__th--numeric">{{ t.th_lessons_per_week }}</th>
                    <th class="zen-table__th zen-table__th--events">{{ t.th_events }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="group.subjects.length === 0">
                    <td colspan="10" class="zen-table__empty">{{ t.no_pieces }}</td>
                  </tr>
                  <tr
                    v-for="row in group.subjects"
                    :key="row.id"
                    class="zen-table__row"
                    :class="{'zen-table__row--unchecked': !isRowChecked(row)}"
                    @click="toggleRow(row)">
                    <td class="zen-table__td zen-table__td--check">
                      <div class="d-checkbox" :class="{'d-checkbox--active': isRowChecked(row)}"></div>
                    </td>
                    <td class="zen-table__td zen-table__td--color">
                      <span class="zen-table__color-dot" :style="{ background: row.color }"></span>
                    </td>
                    <td class="zen-table__td zen-table__td--name">
                      {{ row.subjectName }}
                    </td>
                    <td class="zen-table__td zen-table__td--type">
                      {{ row.typeName }}
                    </td>
                    <td class="zen-table__td">
                      {{ row.teachers }}
                    </td>
                    <td class="zen-table__td">
                      {{ row.rooms }}
                    </td>
                    <td class="zen-table__td">
                      {{ row.otherKlasses }}
                    </td>
                    <td class="zen-table__td zen-table__td--numeric">
                      {{ row.hours }}
                    </td>
                    <td class="zen-table__td zen-table__td--numeric">
                      {{ row.lessonsPerWeek }}
                    </td>
                    <td class="zen-table__td zen-table__td--events" @click.stop>
                      <input
                        type="number"
                        min="0"
                        class="zen-table__input"
                        :value="rowEvents(row)"
                        @change="onEventsInput(row, $event)">
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="zen-export__empty">
              <template v-if="activeRows.length === 0">
                {{ t.no_pieces_in_calendar_type }}
              </template>
              <template v-else>
                {{ t.no_pieces_match_filters }}
              </template>
            </div>
          </div>

          <div class="zen-export__footer">
            <button class="do-bttn" @click="$emit('close')">{{ t.cancel }}</button>
            <button class="do-bttn do-bttn--submit" @click="saveAndExport" :disabled="isExporting">
              {{ isExporting ? t.transferring : t.transfer_to_zen }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import Loader from '../common/Loader.vue'
import Tabs from '../common/Tabs.vue'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import docendo from '../../docendo'
import eventbus from '../../eventbus'

const ZEN_EXPORT_SUBJECT_TYPE_ORDER = [
  'subject',
  'optional',
  'resource',
  'person_resource_visible',
  'person_resource',
  'supplemental',
  'break'
]

const I18N = {
  da: {
    title: 'Vælg brikker til skemalægning',
    select_all: 'Vælg alle',
    deselect_all: 'Fravælg alle',
    tab_count: '{count} af {total} brikker i {scope} valgt',
    total_count: '{count} af {total} skoleårets brikker valgt',
    scope_klass: 'klasserne',
    scope_user: 'personalekalenderne',
    scope_other: 'andre kalendere',
    tab_klass: 'Klasser',
    tab_user: 'Personale',
    tab_other: 'Andre kalendere',
    filter_title: 'Titel',
    filter_type: 'Type',
    filter_calendars_klass: 'Klasser',
    filter_calendars_user: 'Personale',
    filter_calendars_other: 'Kalendere',
    filter_no_type: '(uden type)',
    filter_resource_hidden: 'Ressource (skjult)',
    filter_resource_personnel: 'Ressource (personale)',
    th_type: 'Type',
    th_teachers: 'Personale',
    th_rooms: 'Lokaler',
    th_klasses: 'Klasser',
    th_hours: 'Timer',
    th_lessons_per_week: 'Lek. pr. uge',
    th_events: 'Antal lektioner',
    no_pieces: 'Ingen brikker',
    no_pieces_in_calendar_type: 'Ingen brikker i denne kalendertype',
    no_pieces_match_filters: 'Ingen brikker matcher de valgte filtre',
    cancel: 'Annuller',
    transferring: 'Overfører...',
    transfer_to_zen: 'Overfør til automatisk skemalægning',
    toast_exported_success: 'Data overført til automatisk skemalægning',
    toast_export_failed: 'Noget gik galt ved overførsel',
    subject_types: {
      subject: 'Fag',
      activity: 'Aktivitet',
      available: 'Rådighedstime',
      person_resource: 'Ressource',
      person_resource_visible: 'Ressource (synlig)',
      resource: 'Ressource',
      additional_tasks: 'Øvrige opgaver',
      preparation: 'Forberedelse',
      club: 'SFO',
      without_kids: 'Uden børn',
      frame: 'Temavisning',
      optional: 'Valgfag/Hold',
      supplemental: 'UUV',
      break: 'Pause'
    }
  },
  nb: {
    title: 'Velg brikker til timeplanlegging',
    select_all: 'Velg alle',
    deselect_all: 'Fjern alle',
    tab_count: '{count} av {total} brikker valgt i {scope}',
    total_count: '{count} av {total} brikker i skoleåret valgt',
    scope_klass: 'klassene',
    scope_user: 'ansattkalenderne',
    scope_other: 'andre kalendere',
    tab_klass: 'Klasser',
    tab_user: 'Ansatte',
    tab_other: 'Andre kalendere',
    filter_title: 'Tittel',
    filter_type: 'Type',
    filter_calendars_klass: 'Klasser',
    filter_calendars_user: 'Ansatte',
    filter_calendars_other: 'Kalendere',
    filter_no_type: '(uten type)',
    filter_resource_hidden: 'Ressurs (skjult)',
    filter_resource_personnel: 'Ressurs (ansatte)',
    th_type: 'Type',
    th_teachers: 'Ansatte',
    th_rooms: 'Rom',
    th_klasses: 'Klasser',
    th_hours: 'Timer',
    th_lessons_per_week: 'Lek. pr. uke',
    th_events: 'Antall leksjoner',
    no_pieces: 'Ingen brikker',
    no_pieces_in_calendar_type: 'Ingen brikker i denne kalendertypen',
    no_pieces_match_filters: 'Ingen brikker matcher valgte filtre',
    cancel: 'Avbryt',
    transferring: 'Overfører...',
    transfer_to_zen: 'Overfør til automatisk timeplanlegging',
    toast_exported_success: 'Data overført til automatisk timeplanlegging',
    toast_export_failed: 'Noe gikk galt under overføring',
    subject_types: {
      subject: 'Fag',
      activity: 'Aktivitet',
      available: 'Rådighetstime',
      person_resource: 'Ressurs',
      person_resource_visible: 'Ressurs (synlig)',
      resource: 'Ressurs',
      additional_tasks: 'Øvrige oppgaver',
      preparation: 'Forberedelse',
      club: 'SFO',
      without_kids: 'Uten barn',
      frame: 'Temavisning',
      optional: 'Valgfag/Gruppe',
      supplemental: 'UUV',
      break: 'Pause'
    }
  },
  en: {
    title: 'Select pieces for scheduling',
    select_all: 'Select all',
    deselect_all: 'Deselect all',
    tab_count: '{count} of {total} pieces selected in {scope}',
    total_count: '{count} of {total} pieces for the school year selected',
    scope_klass: 'the classes',
    scope_user: 'the staff calendars',
    scope_other: 'other calendars',
    tab_klass: 'Classes',
    tab_user: 'Staff',
    tab_other: 'Other calendars',
    filter_title: 'Title',
    filter_type: 'Type',
    filter_calendars_klass: 'Classes',
    filter_calendars_user: 'Staff',
    filter_calendars_other: 'Calendars',
    filter_no_type: '(without type)',
    filter_resource_hidden: 'Resource (hidden)',
    filter_resource_personnel: 'Resource (staff)',
    th_type: 'Type',
    th_teachers: 'Staff',
    th_rooms: 'Rooms',
    th_klasses: 'Classes',
    th_hours: 'Hours',
    th_lessons_per_week: 'Les. per week',
    th_events: 'Number of lessons',
    no_pieces: 'No pieces',
    no_pieces_in_calendar_type: 'No pieces in this calendar type',
    no_pieces_match_filters: 'No pieces match the selected filters',
    cancel: 'Cancel',
    transferring: 'Transferring...',
    transfer_to_zen: 'Transfer to automatic scheduling',
    toast_exported_success: 'Data transferred to automatic scheduling',
    toast_export_failed: 'Something went wrong during transfer',
    subject_types: {
      subject: 'Subject',
      activity: 'Activity',
      available: 'On-call',
      person_resource: 'Resource',
      person_resource_visible: 'Resource (visible)',
      resource: 'Resource',
      additional_tasks: 'Other tasks',
      preparation: 'Preparation',
      club: 'Club',
      without_kids: 'Without kids',
      frame: 'Theme',
      optional: 'Optional/Group',
      supplemental: 'UUV',
      break: 'Break'
    }
  }
}

export default {
  components: { Loader, Tabs, DoFiltersFilter },
  props: {
    schoolId: {
      type: Number,
      required: true
    },
    schoolYearId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      activeTab: 'klass',
      isExporting: false,
      localSettings: {},
      selectedSubjectTitleFilters: [],
      selectedSubjectTypeFilters: [],
      selectedCalendarNameFilters: [],
      durationInputMinutes: 45
    }
  },
  watch: {
    activeTab () {
      this.selectedSubjectTitleFilters = []
      this.selectedSubjectTypeFilters = []
      this.selectedCalendarNameFilters = []
    },
    isLoading (val) {
      if (!val) {
        this.syncDurationInputFromSchool()
      }
    }
  },
  computed: {
    isLoading () {
      return this.$store.state.subjects.isLoadingSubjects
    },
    i18n () {
      return I18N
    },
    locale () {
      const l = this.$store.getters.locale
      return I18N[l] ? l : 'en'
    },
    t () {
      return I18N[this.locale]
    },
    tabCountText () {
      return this.formatMessage(this.t.tab_count, {
        count: this.tabCheckedCount,
        total: this.activeRows.length,
        scope: this.activeTabBrikkerScope
      })
    },
    totalCountText () {
      return this.formatMessage(this.t.total_count, {
        count: this.totalCheckedCount,
        total: this.totalCount
      })
    },
    calendarTabs () {
      return [
        { id: 'klass', title: this.t.tab_klass },
        { id: 'user', title: this.t.tab_user },
        { id: 'other', title: this.t.tab_other }
      ]
    },
    activeTabBrikkerScope () {
      if (this.activeTab === 'klass') return this.t.scope_klass
      if (this.activeTab === 'user') return this.t.scope_user
      return this.t.scope_other
    },
    calendarFilterTitle () {
      if (this.activeTab === 'klass') return this.t.filter_calendars_klass
      if (this.activeTab === 'user') return this.t.filter_calendars_user
      return this.t.filter_calendars_other
    },
    subjects () {
      return this.$store.state.subjects.subjects
    },
    school () {
      return this.$store.state.activeSchool
    },
    subjectDefaultDuration () {
      return this.school && this.school.subject_default_duration ? this.school.subject_default_duration : 45
    },
    zenLessonsSchoolYearWeeks () {
      const c = this.school && this.school.country ? String(this.school.country).toUpperCase() : ''
      return c === 'NO' ? 38 : 40
    },
    poolSubjectTypeLabel () {
      const title = this.$store.getters.localPoolTitle
      return title || 'Lokalpulje'
    },
    isAValidStandardDuration () {
      const n = Number(this.durationInputMinutes)
      return /^-?[\d.]+(?:e-?\d+)?$/.test(String(this.durationInputMinutes)) && n > 0 && n < 1440 && n % 5 === 0
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },

    klassRows () {
      return this.buildRows('klass')
    },
    userRows () {
      return this.buildRows('user')
    },
    otherRows () {
      return this.buildRows('other')
    },

    activeRows () {
      if (this.activeTab === 'klass') return this.klassRows
      if (this.activeTab === 'user') return this.userRows
      return this.otherRows
    },

    uniqueSubjectTitles () {
      const names = this.activeRows.map(r => r.subjectName).filter(n => n)
      return [...new Set(names)].sort((a, b) => a.localeCompare(b, 'da'))
    },

    uniqueCalendarNames () {
      const names = this.activeRows.map(r => r.calendarName).filter(n => n)
      return [...new Set(names)].sort((a, b) => a.localeCompare(b, 'da'))
    },

    uniqueSubjectTypeFilterTags () {
      const types = [...new Set(this.activeRows.map(r => r.subjectType || ''))]
      types.sort((a, b) => this.compareZenExportRowsByTypeThenName(
        { subjectType: a, subjectName: '' },
        { subjectType: b, subjectName: '' }
      ))
      return types.map(t => this.subjectTypeFilterOptionLabel(t))
    },

    filteredActiveRows () {
      let rows = this.activeRows
      if (this.selectedSubjectTitleFilters.length > 0) {
        const titles = new Set(this.selectedSubjectTitleFilters)
        rows = rows.filter(r => titles.has(r.subjectName))
      }
      if (this.selectedSubjectTypeFilters.length > 0) {
        const typeTags = new Set(this.selectedSubjectTypeFilters)
        rows = rows.filter(r => typeTags.has(this.subjectTypeFilterOptionLabel(r.subjectType)))
      }
      if (this.selectedCalendarNameFilters.length > 0) {
        const cals = new Set(this.selectedCalendarNameFilters)
        rows = rows.filter(r => cals.has(r.calendarName))
      }
      return rows
    },

    groupedRows () {
      const groups = {}
      const rows = this.filteredActiveRows
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        let g = groups[row.calendarId]
        if (!g) {
          g = {
            calendarId: row.calendarId,
            calendarName: row.calendarName,
            subjects: []
          }
          groups[row.calendarId] = g
        }
        g.subjects.push(row)
      }
      return Object.values(groups)
        .sort((a, b) => (a.calendarName || '').localeCompare(b.calendarName || '', 'da'))
    },

    tabCheckedCount () {
      const rows = this.activeRows
      let n = 0
      for (let i = 0; i < rows.length; i++) {
        if (this.isRowChecked(rows[i])) n++
      }
      return n
    },

    filteredCheckedCount () {
      const rows = this.filteredActiveRows
      let n = 0
      for (let i = 0; i < rows.length; i++) {
        if (this.isRowChecked(rows[i])) n++
      }
      return n
    },

    allChecked () {
      return this.filteredActiveRows.length > 0 && this.filteredCheckedCount === this.filteredActiveRows.length
    },

    totalCount () {
      return this.klassRows.length + this.userRows.length + this.otherRows.length
    },

    totalCheckedCount () {
      let n = 0
      const all = [this.klassRows, this.userRows, this.otherRows]
      for (let t = 0; t < all.length; t++) {
        const rows = all[t]
        for (let i = 0; i < rows.length; i++) {
          if (this.isRowChecked(rows[i])) n++
        }
      }
      return n
    }
  },
  methods: {
    syncDurationInputFromSchool () {
      this.durationInputMinutes = this.subjectDefaultDuration
    },
    onStandardDurationFocus (evt) {
      setTimeout(() => {
        evt.target.select()
      }, 0)
    },
    onStandardDurationBlur () {
      if (!this.isAValidStandardDuration) {
        this.durationInputMinutes = this.subjectDefaultDuration
        return
      }
      if (Number(this.durationInputMinutes) === Number(this.subjectDefaultDuration)) {
        return
      }
      docendo.updateSchool(this.schoolId, { subject_default_duration: this.durationInputMinutes }, (success) => {
        const next = success && success.subject_default_duration != null ? success.subject_default_duration : this.durationInputMinutes
        this.$store.commit('updateActiveSchoolDuration', next)
        this.durationInputMinutes = next
        eventbus.toast = { text: this.$t('vue2.common.saved'), success: true }
      }, () => {
        this.durationInputMinutes = this.subjectDefaultDuration
        eventbus.toast = { text: this.$t('vue2.common.something_went_wrong'), success: false }
      })
    },
    toggleSelectedSubjectTitleFilters (tag) {
      const idx = this.selectedSubjectTitleFilters.indexOf(tag)
      if (idx < 0) {
        this.selectedSubjectTitleFilters.push(tag)
      } else {
        this.selectedSubjectTitleFilters.splice(idx, 1)
      }
    },
    clearSelectedSubjectTitleFilters () {
      this.selectedSubjectTitleFilters = []
    },
    toggleSelectedSubjectTypeFilters (tag) {
      const idx = this.selectedSubjectTypeFilters.indexOf(tag)
      if (idx < 0) {
        this.selectedSubjectTypeFilters.push(tag)
      } else {
        this.selectedSubjectTypeFilters.splice(idx, 1)
      }
    },
    clearSelectedSubjectTypeFilters () {
      this.selectedSubjectTypeFilters = []
    },
    toggleSelectedCalendarNameFilters (tag) {
      const idx = this.selectedCalendarNameFilters.indexOf(tag)
      if (idx < 0) {
        this.selectedCalendarNameFilters.push(tag)
      } else {
        this.selectedCalendarNameFilters.splice(idx, 1)
      }
    },
    clearSelectedCalendarNameFilters () {
      this.selectedCalendarNameFilters = []
    },
    defaultEventsForHours (hours) {
      const lpw = parseFloat(this.$helpers.calcLessonsPerWeek(hours, this.subjectDefaultDuration, this.zenLessonsSchoolYearWeeks)) || 0
      return Math.ceil(lpw)
    },

    defaultCheckedForSubject (subjectType, calendarType) {
      if (calendarType !== 'klass') return false
      return subjectType !== 'activity' &&
        subjectType !== 'frame' &&
        subjectType !== 'additional_tasks'
    },

    isRowChecked (row) {
      const setting = this.localSettings[row.subjectId]
      if (setting) return !!setting.checked
      return this.defaultCheckedForSubject(row.subjectType, row.calendarType)
    },

    rowEvents (row) {
      const setting = this.localSettings[row.subjectId]
      if (setting && setting.events != null) return setting.events
      return row.defaultEvents
    },

    buildRows (calendarType) {
      const rows = []
      const calHash = this.allCalendarsInHash
      const subjects = this.subjects
      const duration = this.subjectDefaultDuration
      const weeks = this.zenLessonsSchoolYearWeeks

      for (let i = 0; i < subjects.length; i++) {
        const subject = subjects[i]
        const cal = calHash[subject.calendar_id]
        if (!cal || cal.type !== calendarType) continue
        if (subject.can_schedule === false) continue

        const subjectType = subject.subject_type || ''
        const lpw = this.$helpers.calcLessonsPerWeek(subject.hours, duration, weeks)

        rows.push({
          id: subject.id,
          subjectId: subject.id,
          subjectType: subjectType,
          calendarType: calendarType,
          calendarId: subject.calendar_id,
          subjectName: subject.name || '',
          calendarName: cal.name || '',
          color: subject.color || '#ccc',
          typeName: this.subjectTypeLabel(subjectType),
          teachers: this.getTeacherNames(subject),
          rooms: this.getRoomNames(subject),
          otherKlasses: this.getOtherKlasses(subject),
          hours: subject.hours,
          lessonsPerWeek: lpw,
          defaultEvents: Math.ceil(parseFloat(lpw) || 0)
        })
      }

      rows.sort((a, b) => this.compareZenExportRowsByTypeThenName(a, b))
      return rows
    },

    collectAllSettings () {
      const settings = {}
      const calHash = this.allCalendarsInHash
      const subjects = this.subjects
      const duration = this.subjectDefaultDuration
      const weeks = this.zenLessonsSchoolYearWeeks

      for (let i = 0; i < subjects.length; i++) {
        const subject = subjects[i]
        const cal = calHash[subject.calendar_id]
        if (!cal) continue
        if (cal.type !== 'klass' && cal.type !== 'user' && cal.type !== 'other') continue
        if (subject.can_schedule === false) continue

        const setting = this.localSettings[subject.id]
        const subjectType = subject.subject_type || ''
        let checked, events
        if (setting) {
          checked = !!setting.checked
          events = setting.events
        } else {
          checked = this.defaultCheckedForSubject(subjectType, cal.type)
          const lpw = parseFloat(this.$helpers.calcLessonsPerWeek(subject.hours, duration, weeks)) || 0
          events = Math.ceil(lpw)
        }
        settings[subject.id] = { checked: checked, events: events }
      }
      return settings
    },

    compareZenExportRowsByTypeThenName (a, b) {
      const typeA = a.subjectType || ''
      const typeB = b.subjectType || ''
      const idxA = ZEN_EXPORT_SUBJECT_TYPE_ORDER.indexOf(typeA)
      const idxB = ZEN_EXPORT_SUBJECT_TYPE_ORDER.indexOf(typeB)
      if (idxA >= 0 && idxB >= 0 && idxA !== idxB) return idxA - idxB
      if (idxA >= 0 && idxB < 0) return -1
      if (idxA < 0 && idxB >= 0) return 1
      if (idxA < 0 && idxB < 0) {
        const cmpType = typeA.localeCompare(typeB, 'da')
        if (cmpType !== 0) return cmpType
      }
      const nameA = a.subjectName || ''
      const nameB = b.subjectName || ''
      return nameA.localeCompare(nameB, 'da')
    },

    formatMessage (template, params) {
      let s = template || ''
      Object.keys(params).forEach(k => {
        s = s.split('{' + k + '}').join(params[k])
      })
      return s
    },

    subjectTypeLabel (type) {
      if (!type) return ''
      if (type === 'pool') return this.poolSubjectTypeLabel
      const label = this.t.subject_types[type]
      return label || type
    },

    subjectTypeFilterOptionLabel (type) {
      if (!type) return this.t.filter_no_type
      if (type === 'resource') return this.t.filter_resource_hidden
      if (type === 'person_resource') return this.t.filter_resource_personnel
      return this.subjectTypeLabel(type)
    },

    getTeacherNames (subject) {
      if (!subject.cals || !subject.cals.length) return ''
      return subject.cals
        .filter(c => c.calendar_type === 'user')
        .map(c => c.name)
        .join(', ')
    },

    getRoomNames (subject) {
      if (!subject.cals || !subject.cals.length) return ''
      return subject.cals
        .filter(c => c.calendar_type === 'room')
        .map(c => c.name)
        .join(', ')
    },

    getOtherKlasses (subject) {
      if (!subject.calendar_ids || !subject.calendar_ids.length) return ''
      const calHash = this.allCalendarsInHash
      const primaryId = subject.calendar_id
      return subject.calendar_ids
        .filter(id => id !== primaryId && calHash[id] && calHash[id].type === 'klass')
        .map(id => calHash[id].name)
        .join(', ')
    },

    toggleRow (row) {
      const wasChecked = this.isRowChecked(row)
      const events = this.rowEvents(row)
      this.$set(this.localSettings, row.subjectId, {
        checked: !wasChecked,
        events: events
      })
    },

    onEventsInput (row, event) {
      const val = parseInt(event.target.value, 10)
      const current = this.localSettings[row.subjectId]
      if (current) {
        this.$set(this.localSettings, row.subjectId, {
          checked: current.checked,
          events: isNaN(val) ? 0 : val
        })
      } else {
        this.$set(this.localSettings, row.subjectId, {
          checked: true,
          events: isNaN(val) ? 0 : val
        })
      }
    },

    toggleAll () {
      const shouldCheck = !this.allChecked
      const rows = this.filteredActiveRows
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        this.$set(this.localSettings, row.subjectId, {
          checked: shouldCheck,
          events: this.rowEvents(row)
        })
      }
    },

    toggleGroup (group) {
      const allChecked = this.isGroupAllChecked(group)
      for (let i = 0; i < group.subjects.length; i++) {
        const row = group.subjects[i]
        this.$set(this.localSettings, row.subjectId, {
          checked: !allChecked,
          events: this.rowEvents(row)
        })
      }
    },

    isGroupAllChecked (group) {
      if (group.subjects.length === 0) return false
      for (let i = 0; i < group.subjects.length; i++) {
        if (!this.isRowChecked(group.subjects[i])) return false
      }
      return true
    },

    saveAndExport () {
      this.isExporting = true
      this.$store.dispatch('trackEvent', 'zen_export_save')
      const settings = this.collectAllSettings()
      docendo.exportToZen(this.schoolId, this.schoolYearId, settings, () => {
        eventbus.toast = { text: this.t.toast_exported_success, success: true }
        this.isExporting = false
        this.$emit('exported')
      }, () => {
        eventbus.toast = { text: this.t.toast_export_failed, success: false }
        this.isExporting = false
      })
    },

    handleKeydown (event) {
      if (event.key === 'Escape') {
        this.$emit('close')
      }
    }
  },
  mounted () {
    this.$store.dispatch('readSubjects', this.schoolYearId)
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped>
.zen-export-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.zen-export {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f6f9fc;
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  animation: fadeUp 0.25s ease;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.zen-export__header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.zen-export__header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #3e4450;
}

.zen-export__header-actions {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.zen-export__duration {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  max-width: 100%;
}

.zen-export__duration-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.zen-export__duration-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 12px;
  font-weight: 600;
  color: #697386;
  white-space: nowrap;
}

.zen-export__duration-info {
  cursor: help;
  font-size: 13px;
  color: #a0aec0;
  line-height: 1;
  font-style: normal;
}

.zen-export__duration-inputwrap {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.zen-export__duration-input {
  width: 56px;
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 14px;
  text-align: right;
}

.zen-export__duration-input:focus {
  outline: none;
  border-color: #468ad7;
  box-shadow: 0 0 0 2px #468ad733;
}

.zen-export__duration-suffix {
  font-size: 13px;
  color: #3e4450;
}

.zen-export__duration-error {
  margin: 0;
  font-size: 12px;
  color: #c53030;
}

.zen-export__close {
  cursor: pointer;
  font-size: 1.1rem;
  color: #8898aa;
  padding: 0.25rem;
  transition: color 0.15s;
}

.zen-export__close:hover {
  color: #3e4450;
}

.zen-export__tabs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.zen-export__count {
  font-style: italic;
  font-size: 12px;
  color: #8898aa;
}

.zen-export__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.zen-export__toolbar-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.zen-export__toggle-all {
  color: #468ad7;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}

.zen-export__toggle-all:hover {
  color: #3e4450;
}

.zen-export__body {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem 1.5rem;
  min-height: 0;
}

.zen-export__tables {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.zen-export__empty {
  padding: 3rem;
  text-align: center;
  color: #8898aa;
  font-style: italic;
}

/* --- Grouped table --- */
.zen-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.zen-table thead {
  border-bottom: 1px solid #e2e8f0;
}

.zen-table__th {
  padding: 0.5rem;
  font-size: 13px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.zen-table__th--check {
  width: 42px;
  cursor: pointer;
  text-align: center;
}

.zen-table__th--check:hover .d-checkbox {
  box-shadow: 0 0 1px 2px #468ad791;
}

.zen-table__th--name {
  color: #09090b;
  font-weight: 700;
}

.zen-table__th--events {
  text-align: right;
  width: 120px;
  color: #8898aa;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
}

.zen-table__empty {
  padding: 0.75rem;
  color: #8898aa;
  font-style: italic;
  font-size: 13px;
}

.zen-table__row {
  cursor: pointer;
  transition: background-color 0.1s;
}

.zen-table__row:hover {
  background-color: #f0f5ff;
}

.zen-table__row--unchecked {
  opacity: 0.5;
}

.zen-table__td {
  padding: 0.4rem 0.5rem;
  border-left: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
}

.zen-table__td--check {
  width: 42px;
  text-align: center;
}

.zen-table__td--check:hover .d-checkbox {
  box-shadow: 0 0 1px 2px #468ad791;
}

.zen-table__td--color {
  width: 24px;
  padding-right: 0;
}

.zen-table__color-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  vertical-align: middle;
}

.zen-table__td--name {
  font-weight: 500;
}

.zen-table__td--type {
  font-size: 12px;
  color: #697386;
  white-space: nowrap;
}

.zen-table__th--numeric,
.zen-table__td--numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.zen-table__td--events {
  text-align: right;
  width: 120px;
}

.zen-table__input {
  width: 72px;
  padding: 3px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
  text-align: right;
}

.zen-table__input:focus {
  outline: none;
  border-color: #468ad7;
  box-shadow: 0 0 0 2px #468ad733;
}

/* --- Footer --- */
.zen-export__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

.zen-export__footer .do-bttn:disabled,
.zen-export__footer .bttn:disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// ZenExport.vue?4b320b7d